import { useState } from 'react';
import tw from 'twin.macro';

import { PlusIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/base';
import { useDeleteTemplateMutation, useGetTemplatesQuery } from '@/domains/template';
import { GetTemplatesQuery, Roles } from '@/libs/graphql';
import { useBackofficeContext } from '@/providers/Backoffice';

import DeleteTemplateModal from './DeleteTemplateModal';
import TemplatesTable from './TemplatesTable';

type Template = NonNullable<GetTemplatesQuery['getTemplates']>[number];

const TemplatesTab = () => {
  const navigate = useNavigate();
  const { user } = useBackofficeContext();
  const isViewOnly = user?.roles?.includes(Roles.ViewOnly) ?? false;
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data, loading: listLoading, refetch } = useGetTemplatesQuery({
    fetchPolicy: 'network-only',
  });

  const [deleteTemplate, { loading: deleteLoading }] = useDeleteTemplateMutation({
    onCompleted: (result) => {
      const res = result.deleteTemplate;
      if (res.__typename === 'DeleteTemplateInputError') {
        // e.g. the template is still used by an unfinished interview session.
        setDeleteError(res.message);
      } else {
        setDeleteTarget(null);
        setDeleteError(null);
        refetch();
      }
    },
  });

  const templates = (data?.getTemplates ?? []) as Template[];

  const handleDeleteConfirm = () => {
    if (deleteTarget) {
      deleteTemplate({ variables: { templateId: deleteTarget.id } });
    }
  };

  return (
    <div tw="flex flex-col gap-4">
      <div tw="flex items-center justify-between">
        <p tw="text-sm text-gray-500">
          {templates.length > 0
            ? `${templates.length} ชุดคำถาม`
            : 'ยังไม่มีชุดคำถาม'}
        </p>
        {!isViewOnly && (
          <Button
            isSmall
            onClick={() => navigate('/backoffice/configuration/templates/create')}
            tw="flex items-center gap-1 px-4 py-2"
          >
            <PlusIcon tw="w-4 h-4" />
            สร้างชุดคำถาม
          </Button>
        )}
      </div>

      {listLoading ? (
        <div tw="py-8 text-center text-gray-400 text-sm">กำลังโหลด...</div>
      ) : (
        <TemplatesTable
          templates={templates}
          isViewOnly={isViewOnly}
          onDelete={(template) => {
            setDeleteError(null);
            setDeleteTarget(template);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteTemplateModal
          template={deleteTarget}
          loading={deleteLoading}
          errorMessage={deleteError ?? undefined}
          onConfirm={handleDeleteConfirm}
          onClose={() => {
            setDeleteTarget(null);
            setDeleteError(null);
          }}
        />
      )}
    </div>
  );
};

export default TemplatesTab;
